import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const isNavigatingRef = useRef(false);
  const prevPathRef = useRef(pathname);

  // Check if user prefers reduced motion
  const prefersReducedMotion = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  // Get header height for offset calculations
  const getHeaderHeight = () => {
    const header = document.querySelector('header');
    return header ? header.offsetHeight : 80;
  };

  // Focus on main heading for accessibility
  const focusMainHeading = () => {
    setTimeout(() => {
      const mainHeading = document.querySelector('h1');
      if (mainHeading) {
        mainHeading.setAttribute('tabindex', '-1');
        mainHeading.focus({ preventScroll: true });
      }
    }, 100);
  };

  useEffect(() => {
    const scrollToPosition = (top, smooth = true) => {
      const behavior = prefersReducedMotion() || !smooth ? 'instant' : 'smooth';
      window.scrollTo({
        top: Math.max(0, top),
        left: 0,
        behavior
      });
    };

    const scrollToHash = (hash) => {
      if (!hash) return false;
      const elementId = hash.replace('#', '');
      const element = document.getElementById(elementId);
      if (element) {
        const headerHeight = getHeaderHeight();
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - headerHeight - 20;
        scrollToPosition(offsetPosition);
        setTimeout(() => {
          element.setAttribute('tabindex', '-1');
          element.focus({ preventScroll: true });
        }, 100);
        return true;
      }
      return false;
    };

    isNavigatingRef.current = true;

    const timeoutId = setTimeout(() => {
      if (hash) {
        scrollToHash(hash);
      }
      else if (pathname !== prevPathRef.current || isNavigatingRef.current) {
        scrollToPosition(0);
        focusMainHeading();
      }

      prevPathRef.current = pathname;
      isNavigatingRef.current = false;
    }, 10);

    return () => clearTimeout(timeoutId);
  }, [pathname, hash]);

  useEffect(() => {
    const handlePopState = () => {
      isNavigatingRef.current = false;
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return null;
};

export default ScrollToTop;
